/**
 * Rent roll import — pure materialization logic.
 *
 * Everything that turns a parsed rent roll into wizard state lives here rather
 * than in the React component, so the arithmetic (lease-end to vacate month,
 * layout naming, Section 8 handling) is unit testable on its own.
 *
 * Nothing here touches the backend or the Google Sheet: the existing save paths
 * persist whatever we put into wizard state, unchanged.
 */
import { MarketRentAssumption, suggestLayoutName } from './floorPlans';

// ── Shapes returned by POST /api/rent_roll/parse ───────────────────────────

export interface ParsedCharge {
  class: string;
  amount: number;
}

export interface ParsedRecord {
  unit_label: string;
  type: string;
  sqft: number | null;
  market_rent: number | null;
  current_rent_total: number | null;
  charges: ParsedCharge[];
  lease_start: string | null;
  lease_end: string | null;
  move_out: string | null;
  status: string | null;
  status_class: string | null;
  vacant: boolean;
  excluded: boolean;
  is_section8: boolean;
  charge_total?: number;
  /**
   * How many units this record stands for. 1 for a per-unit row; more when the
   * row is a floor plan out of a unit-mix table. Absent on older responses,
   * which is why every read of it defaults to 1.
   */
  unit_count?: number;
}

export interface ParsedFloorPlan {
  type: string;
  sqft: number | null;
  unit_count: number;
  market_rent: number | null;
  unit_labels: string[];
}

export interface ParsedOutlier {
  unit_label: string;
  type: string;
  sqft: number | null;
  market_rent: number | null;
  plan_market_rent: number | null;
}

export interface ReconciliationCheck {
  name: string;
  label: string;
  parsed: number | null;
  reported: number | null;
  ok: boolean;
  detail?: string[];
}

export interface ParseException {
  kind: string;
  label: string;
  count: number;
  unit_labels: string[];
}

/** 'unit' — one record, one unit. 'plan' — one record, `unit_count` units. */
export type RowsAre = 'unit' | 'plan';

export interface RentRollParseResponse {
  records: ParsedRecord[];
  unit_count: number;
  floor_plans: ParsedFloorPlan[];
  outliers: ParsedOutlier[];
  exceptions: ParseException[];
  reconciliation: { checks: ReconciliationCheck[]; all_ok: boolean; unmatched: string[] };
  recipe_source: string | null;
  vendor: string | null;
  rows_are?: RowsAre;
  /** Which page of a PDF this was read from, when it came from one. */
  page?: number | null;
}

// ── Decisions the reviewer makes before importing ──────────────────────────

export type MarketRentDecision = 'use_rent_roll' | 'enter_my_own';
export type LeaseExpiryDecision = 'market_adjustment' | 'vacate' | 'keep';
export type VacantUnitDecision = 're_lease' | 'exclude';

export interface ImportDecisions {
  marketRents: MarketRentDecision;
  atExpiration: LeaseExpiryDecision;
  vacantUnits: VacantUnitDecision;
}

export const DEFAULT_DECISIONS: ImportDecisions = {
  marketRents: 'use_rent_roll',
  atExpiration: 'market_adjustment',
  vacantUnits: 're_lease',
};

export const IMPORT_SOURCE = 'rent_roll';
export const HAP_RENT_TYPE = 'HAP';

export interface ImportedUnit {
  id: string;
  rent_type: string;
  vacate_flag: number;
  layout: string;
  square_feet: number;
  vacate_month: number;
  current_rent: number | null;
  unit_label?: string | null;
  lease_end?: string | null;
  market_rent_override?: number | null;
  is_section8?: boolean | null;
  import_source?: string | null;
}

export interface GrowthRate {
  name: string;
  value: number;
  type: string;
}

// ── Dates ──────────────────────────────────────────────────────────────────

/**
 * Parse the date formats this app actually holds: the wizard stores Model
 * Start Date as MM/DD/YYYY, while lease_end arrives from the parser as ISO.
 */
export function parseFlexibleDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  }
  return null;
}

/**
 * Whole calendar months from start to end. Day-of-month is ignored: what
 * matters for a model is which month the lease lands in, not how far into it.
 */
export function monthsBetween(start: Date, end: Date): number {
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
}

function isMonthToMonth(record: ParsedRecord): boolean {
  // Status codes are unreliable in these exports — this fixture has 24 units
  // paying a month-to-month fee but only one carrying MTM status — so the
  // charge is the stronger signal and is checked first.
  if (record.charges.some((c) => c.class === 'mtm_fee')) return true;
  return (record.status_class || '').toLowerCase() === 'mtm';
}

/**
 * The model month in which a unit's lease expires. Month-to-month tenants and
 * units with no lease end roll immediately, so they land on month 1, and no
 * unit is ever placed before the model starts.
 */
export function deriveVacateMonth(
  record: ParsedRecord,
  modelStartDate: string | null | undefined
): number {
  if (isMonthToMonth(record) || !record.lease_end) return 1;
  const start = parseFlexibleDate(modelStartDate);
  const end = parseFlexibleDate(record.lease_end);
  if (!start || !end) return 1;
  return Math.max(1, monthsBetween(start, end));
}

// ── Layout naming ──────────────────────────────────────────────────────────

export function planKey(type: string, sqft: number | null): string {
  return `${type}|${sqft === null || sqft === undefined ? '' : sqft}`;
}

/**
 * Map each (type, SF) plan to the layout string the model will use.
 *
 * Layout strings are INDEX/MATCH keys in the generated sheet and must be
 * unique. A type appearing at one size keeps its plain name; a type spanning
 * several sizes gets the established "Name (820 SF)" disambiguation, which is
 * exactly the case this data has — 2X1 at 850 and 900, 2X2 L at three sizes.
 */
export function deriveLayoutNames(plans: ParsedFloorPlan[]): Record<string, string> {
  const sizesPerType: Record<string, number> = {};
  plans.forEach((plan) => {
    sizesPerType[plan.type] = (sizesPerType[plan.type] || 0) + 1;
  });

  const used: Record<string, boolean> = {};
  const names: Record<string, string> = {};

  plans.forEach((plan) => {
    const multipleSizes = (sizesPerType[plan.type] || 0) > 1;
    let name =
      multipleSizes && plan.sqft
        ? suggestLayoutName(plan.type, plan.sqft)
        : plan.type;
    if (!name) name = plan.type || 'Unit';

    // Uniqueness is a hard requirement, so fall back rather than collide.
    if (used[name.toLowerCase()]) {
      const disambiguated = plan.sqft ? suggestLayoutName(plan.type, plan.sqft) : '';
      name = disambiguated && !used[disambiguated.toLowerCase()] ? disambiguated : name;
      let suffix = 2;
      while (used[name.toLowerCase()]) {
        name = `${plan.type} (${suffix})`;
        suffix += 1;
      }
    }

    used[name.toLowerCase()] = true;
    names[planKey(plan.type, plan.sqft)] = name;
  });

  return names;
}

// ── Materialize ────────────────────────────────────────────────────────────

const VACATE_FLAG_BY_DECISION: Record<LeaseExpiryDecision, number> = {
  market_adjustment: 2,
  vacate: 1,
  keep: 0,
};

export interface MaterializeInput {
  response: RentRollParseResponse;
  decisions: ImportDecisions;
  growthRates: GrowthRate[];
  modelStartDate: string | null | undefined;
  makeId: (index: number) => string;
}

export interface MaterializeResult {
  units: ImportedUnit[];
  marketRentAssumptions: MarketRentAssumption[];
  growthRates: GrowthRate[];
  /** Units left out of the rent roll, so the UI can say so rather than lose them silently. */
  skipped: { excluded: string[]; vacant: string[] };
}

/**
 * Turn floor-plan rows into the units they stand for.
 *
 * A unit-mix table says "2x1.5 Bedroom — 47 of them". Materializing one unit
 * per RECORD would import a 74-unit property as 7 and look like it worked, so
 * every record is expanded to its own count before anything downstream sees it.
 * A per-unit row has a count of 1 and passes through untouched.
 *
 * The copies carry NO unit label, because the source never named them. That
 * follows the unit-mix generator, which also leaves generated units unlabelled
 * — inventing "2x1.5 Bedroom #12" would be manufacturing an identifier that
 * exists nowhere in the document.
 */
export function expandPlanRows(records: ParsedRecord[]): ParsedRecord[] {
  const out: ParsedRecord[] = [];
  records.forEach((record) => {
    const count = Math.max(1, Math.floor(record.unit_count ?? 1));
    if (count === 1) {
      out.push(record);
      return;
    }
    for (let i = 0; i < count; i += 1) {
      out.push({ ...record, unit_label: '', unit_count: 1 });
    }
  });
  return out;
}

export function materializeRentRoll(input: MaterializeInput): MaterializeResult {
  const { response, decisions, growthRates, modelStartDate, makeId } = input;

  const layoutNames = deriveLayoutNames(response.floor_plans);
  const useRentRollMarketRents = decisions.marketRents === 'use_rent_roll';

  const marketRentAssumptions: MarketRentAssumption[] = response.floor_plans.map((plan) => ({
    layout: layoutNames[planKey(plan.type, plan.sqft)],
    pf_rent: useRentRollMarketRents ? plan.market_rent || 0 : 0,
    square_feet: plan.sqft ?? null,
  }));

  const planRentByKey: Record<string, number | null> = {};
  response.floor_plans.forEach((plan) => {
    planRentByKey[planKey(plan.type, plan.sqft)] = plan.market_rent;
  });

  const defaultRentType =
    growthRates.filter((r) => r.type === 'rental')[0]?.name || growthRates[0]?.name || 'Fair Market';

  const skipped = { excluded: [] as string[], vacant: [] as string[] };

  // Down / off-line units are excluded from the roll by default: the report
  // itself excludes them from its occupancy totals.
  const keep = expandPlanRows(response.records).filter((record) => {
    if (record.excluded) {
      skipped.excluded.push(record.unit_label);
      return false;
    }
    if (record.vacant && decisions.vacantUnits === 'exclude') {
      skipped.vacant.push(record.unit_label);
      return false;
    }
    return true;
  });

  const ordered = keep.slice().sort((a, b) =>
    String(a.unit_label).localeCompare(String(b.unit_label), undefined, { numeric: true })
  );

  const expiryFlag = VACATE_FLAG_BY_DECISION[decisions.atExpiration];

  const units: ImportedUnit[] = ordered.map((record, index) => {
    const key = planKey(record.type, record.sqft);
    const planRent = planRentByKey[key];
    const isOutlier =
      record.market_rent !== null && planRent !== null && record.market_rent !== planRent;

    // A vacant unit re-leases at market in month 1 regardless of what happens
    // to occupied units at their lease expiration.
    const vacant = record.vacant;

    return {
      id: makeId(index),
      unit_label: record.unit_label,
      layout: layoutNames[key],
      square_feet: record.sqft || 0,
      current_rent: vacant ? 0 : record.current_rent_total ?? 0,
      rent_type: record.is_section8 ? HAP_RENT_TYPE : defaultRentType,
      vacate_flag: vacant ? 1 : expiryFlag,
      vacate_month: vacant ? 1 : deriveVacateMonth(record, modelStartDate),
      lease_end: record.lease_end || null,
      is_section8: record.is_section8,
      import_source: IMPORT_SOURCE,
      market_rent_override:
        useRentRollMarketRents && isOutlier ? record.market_rent : null,
    };
  });

  // Subsidised units get their own rent type so their growth can be set apart
  // from market units; create it at 0% if the model has no HAP rate yet.
  let nextGrowthRates = growthRates;
  const needsHap = units.some((u) => u.rent_type === HAP_RENT_TYPE);
  const hasHap = growthRates.some(
    (r) => r.type === 'rental' && r.name.trim().toLowerCase() === HAP_RENT_TYPE.toLowerCase()
  );
  if (needsHap && !hasHap) {
    nextGrowthRates = growthRates.concat([{ name: HAP_RENT_TYPE, value: 0, type: 'rental' }]);
  }

  return { units, marketRentAssumptions, growthRates: nextGrowthRates, skipped };
}

/**
 * Recompute vacate months for previously imported units after the Model Start
 * Date changes. Only touches rent-roll units that carry a lease end; anything
 * hand-entered or generated keeps whatever the user set.
 */
export function reDeriveVacateMonths<T extends ImportedUnit>(
  units: T[],
  modelStartDate: string | null | undefined
): T[] {
  const start = parseFlexibleDate(modelStartDate);
  if (!start) return units;

  let changed = false;
  const next = units.map((unit) => {
    if (unit.import_source !== IMPORT_SOURCE || !unit.lease_end) return unit;
    const end = parseFlexibleDate(unit.lease_end);
    if (!end) return unit;
    const month = Math.max(1, monthsBetween(start, end));
    if (month === unit.vacate_month) return unit;
    changed = true;
    return { ...unit, vacate_month: month };
  });

  // Returning the same reference when nothing moved keeps this safe to call
  // from an effect without looping.
  return changed ? next : units;
}
